/**
 * `/admin/gantt` — la roadmap si scrive qui.
 *
 * Fino al 21-08-2026 il Gantt viveva nel seed: 206 righe di Python, e per
 * spostare una data serviva un deploy. Con quel costo nessuno l'ha spostata —
 * ad agosto il grafico mostrava ancora la roadmap del 2 maggio.
 *
 * Le tre famiglie hanno regole diverse e la pagina le separa invece di
 * chiedere all'utente di ricordarsele:
 *
 * - **Barre** — costruzione e uso. Possono appartenere a un modulo o stare da
 *   sole (collaudo, formazione): è la novità della migration 0063.
 * - **Milestone** — un punto nel tempo, quindi senza data di fine. Il campo
 *   proprio non compare.
 * - **Bande** — finestre esterne (fiera, ferie), sfondo grigio sul grafico.
 */

import { AlertTriangle, ArrowLeft, Eye, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminGantt,
  useAggiornaElementoGantt,
  useCreaElementoGantt,
  useEliminaElementoGantt,
} from "@/hooks/useAdminGantt";
import { useMe } from "@/hooks/useMe";
import { useModuli } from "@/hooks/useModuli";
import { ApiError } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { GanttElemento, GanttTipo, Modulo } from "@/types/api";

const INPUT =
  "w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

const TIPO_LABEL: Record<GanttTipo, string> = {
  barra_costruzione: "costruzione",
  barra_uso: "in uso",
  punto_milestone: "milestone",
  banda_temporale: "banda",
};

const FAMIGLIE: {
  titolo: string;
  spiegazione: string;
  tipi: GanttTipo[];
  nuovo: GanttTipo;
}[] = [
  {
    titolo: "Barre",
    spiegazione:
      "Una corsia per etichetta: costruzione e «in uso» con lo stesso nome finiscono sulla stessa riga. Senza modulo è una fase libera — collaudo, formazione.",
    tipi: ["barra_costruzione", "barra_uso"],
    nuovo: "barra_costruzione",
  },
  {
    titolo: "Milestone",
    spiegazione:
      "Punti nel tempo, senza data di fine. Dal 21-08 il go-live non sta più qui: è per modulo, si imposta in «Stato moduli».",
    tipi: ["punto_milestone"],
    nuovo: "punto_milestone",
  },
  {
    titolo: "Finestre esterne",
    spiegazione:
      "Periodi in cui una delle due parti non c'è (fiera, ferie). Sul grafico sono lo sfondo grigio tratteggiato.",
    tipi: ["banda_temporale"],
    nuovo: "banda_temporale",
  },
];

export function AdminGanttPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: elementi = [], isLoading } = useAdminGantt();
  const { data: moduli = [] } = useModuli();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Timeline di progetto
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          La roadmap che il cliente vede nella Panoramica e in{" "}
          <Link to="/progetto/gantt" className="underline hover:text-foreground">
            Timeline
          </Link>
          . Quello che scrivi qui è la verità: i deploy non la riscrivono più.
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="space-y-8">
          {FAMIGLIE.map((f) => (
            <Famiglia
              key={f.titolo}
              famiglia={f}
              elementi={elementi.filter((e) => f.tipi.includes(e.tipo))}
              moduli={moduli}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function Famiglia({
  famiglia,
  elementi,
  moduli,
}: {
  famiglia: (typeof FAMIGLIE)[number];
  elementi: GanttElemento[];
  moduli: Modulo[];
}) {
  const [nuovo, setNuovo] = useState(false);
  const ordinati = useMemo(
    () => [...elementi].sort((a, b) => a.ordine - b.ordine || a.data_inizio.localeCompare(b.data_inizio)),
    [elementi]
  );

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-0.5">
          <h2 className="text-base font-semibold">{famiglia.titolo}</h2>
          <p className="text-xs text-muted-foreground max-w-2xl">{famiglia.spiegazione}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setNuovo((v) => !v)}>
          <Plus className="size-3.5" />
          Aggiungi
        </Button>
      </div>

      {nuovo && (
        <NuovoElemento
          tipoDefault={famiglia.nuovo}
          tipi={famiglia.tipi}
          moduli={moduli}
          onFatto={() => setNuovo(false)}
        />
      )}

      {ordinati.length === 0 ? (
        <p className="rounded-lg border bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          Niente in questa famiglia.
        </p>
      ) : (
        <ul className="rounded-lg border bg-card divide-y">
          {ordinati.map((e) => (
            <RigaElemento key={e.id} elemento={e} moduli={moduli} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RigaElemento({
  elemento: e,
  moduli,
}: {
  elemento: GanttElemento;
  moduli: Modulo[];
}) {
  const aggiorna = useAggiornaElementoGantt();
  const elimina = useEliminaElementoGantt();
  const [conferma, setConferma] = useState(false);
  const [etichetta, setEtichetta] = useState(e.etichetta);
  const [errore, setErrore] = useState<string | null>(null);

  const isPending = aggiorna.isPending || elimina.isPending;
  const modulo = e.modulo_id ? moduli.find((m) => m.id === e.modulo_id) : undefined;
  const haFine = e.tipo !== "punto_milestone";

  function patch(body: Record<string, unknown>) {
    setErrore(null);
    aggiorna.mutate(
      { id: e.id, body },
      {
        onError: (err) =>
          setErrore(err instanceof ApiError ? String(err.message) : "Modifica rifiutata."),
      }
    );
  }

  return (
    <li className={cn("px-4 py-3 space-y-2", isPending && "opacity-50")}>
      <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
        <Badge variant="outline" className="shrink-0 mt-1.5 text-[10px] uppercase font-mono">
          {TIPO_LABEL[e.tipo]}
        </Badge>

        <input
          type="text"
          value={etichetta}
          onChange={(ev) => setEtichetta(ev.target.value)}
          onBlur={() => {
            const v = etichetta.trim();
            if (!v || v === e.etichetta) return setEtichetta(e.etichetta);
            patch({ etichetta: v });
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") ev.currentTarget.blur();
          }}
          disabled={isPending}
          aria-label="Etichetta"
          className={cn(INPUT, "flex-1 min-w-[180px] font-medium")}
        />

        <label className="flex items-center gap-1.5 shrink-0">
          <span className="text-[11px] text-muted-foreground">dal</span>
          <input
            type="date"
            value={e.data_inizio.slice(0, 10)}
            onChange={(ev) => patch({ data_inizio: ev.target.value })}
            disabled={isPending}
            aria-label={`Inizio di ${e.etichetta}`}
            className={cn(INPUT, "w-[145px]")}
          />
        </label>

        {haFine && (
          <label className="flex items-center gap-1.5 shrink-0">
            <span className="text-[11px] text-muted-foreground">al</span>
            <input
              type="date"
              value={e.data_fine?.slice(0, 10) ?? ""}
              onChange={(ev) => patch({ data_fine: ev.target.value || null })}
              disabled={isPending}
              aria-label={`Fine di ${e.etichetta}`}
              className={cn(INPUT, "w-[145px]")}
              title="Vuoto = la barra prosegue oltre il grafico (è il caso di «in uso»)"
            />
          </label>
        )}

        {e.tipo.startsWith("barra") && (
          <select
            value={e.modulo_id ?? ""}
            onChange={(ev) => patch({ modulo_id: ev.target.value || null })}
            disabled={isPending}
            aria-label={`Modulo di ${e.etichetta}`}
            className={cn(INPUT, "w-[190px] shrink-0")}
          >
            <option value="">Fase libera</option>
            {moduli.map((m) => (
              <option key={m.id} value={m.id}>
                {m.codice} · {m.nome}
              </option>
            ))}
          </select>
        )}

        <input
          type="number"
          min={0}
          max={999}
          value={e.ordine}
          onChange={(ev) => patch({ ordine: Number(ev.target.value) })}
          disabled={isPending}
          aria-label={`Ordine di ${e.etichetta}`}
          title="Ordine della corsia nel grafico"
          className={cn(INPUT, "w-[70px] shrink-0 tabular-nums")}
        />

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            disabled={isPending}
            onClick={() => patch({ is_visibile_cliente: !e.is_visibile_cliente })}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-50",
              e.is_visibile_cliente
                ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
                : "bg-muted text-muted-foreground border-muted-foreground/20"
            )}
            title={e.is_visibile_cliente ? "Visibile al cliente" : "Interno"}
          >
            {e.is_visibile_cliente ? (
              <Eye className="size-3.5" />
            ) : (
              <EyeOff className="size-3.5" />
            )}
          </button>

          {conferma ? (
            <>
              <Button
                size="sm"
                variant="destructive"
                disabled={isPending}
                onClick={() => elimina.mutate(e.id)}
              >
                Conferma
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConferma(false)}>
                No
              </Button>
            </>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              disabled={isPending}
              onClick={() => setConferma(true)}
              aria-label={`Togli dal piano ${e.etichetta}`}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {modulo && (
        <p className="text-[11px] text-muted-foreground pl-1">
          corsia del modulo <span className="font-mono">{modulo.codice}</span>
        </p>
      )}
      {errore && (
        <p className="text-xs text-destructive inline-flex items-center gap-1.5">
          <AlertTriangle className="size-3.5" />
          {errore}
        </p>
      )}
    </li>
  );
}

function NuovoElemento({
  tipoDefault,
  tipi,
  moduli,
  onFatto,
}: {
  tipoDefault: GanttTipo;
  tipi: GanttTipo[];
  moduli: Modulo[];
  onFatto: () => void;
}) {
  const crea = useCreaElementoGantt();
  const [tipo, setTipo] = useState<GanttTipo>(tipoDefault);
  const [etichetta, setEtichetta] = useState("");
  const [inizio, setInizio] = useState("");
  const [fine, setFine] = useState("");
  const [moduloId, setModuloId] = useState("");
  const [ordine, setOrdine] = useState(10);
  const [errore, setErrore] = useState<string | null>(null);

  const haFine = tipo !== "punto_milestone";
  const valido = etichetta.trim() !== "" && inizio !== "";

  function salva() {
    if (!valido) return;
    setErrore(null);
    crea.mutate(
      {
        tipo,
        etichetta: etichetta.trim(),
        data_inizio: inizio,
        data_fine: haFine && fine ? fine : null,
        modulo_id: tipo.startsWith("barra") && moduloId ? moduloId : null,
        ordine,
      },
      {
        onSuccess: onFatto,
        onError: (err) =>
          setErrore(err instanceof ApiError ? String(err.message) : "Creazione rifiutata."),
      }
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        {tipi.length > 1 && (
          <label className="space-y-1">
            <span className="text-[11px] text-muted-foreground block">Tipo</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as GanttTipo)}
              className={cn(INPUT, "w-[150px]")}
              aria-label="Tipo di elemento"
            >
              {tipi.map((t) => (
                <option key={t} value={t}>
                  {TIPO_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="space-y-1 flex-1 min-w-[200px]">
          <span className="text-[11px] text-muted-foreground block">Etichetta *</span>
          <input
            type="text"
            value={etichetta}
            onChange={(e) => setEtichetta(e.target.value)}
            placeholder="Collaudo e formazione"
            className={INPUT}
            aria-label="Etichetta del nuovo elemento"
          />
        </label>

        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground block">Dal *</span>
          <input
            type="date"
            value={inizio}
            onChange={(e) => setInizio(e.target.value)}
            className={cn(INPUT, "w-[145px]")}
            aria-label="Data di inizio"
          />
        </label>

        {haFine && (
          <label className="space-y-1">
            <span className="text-[11px] text-muted-foreground block">Al</span>
            <input
              type="date"
              value={fine}
              onChange={(e) => setFine(e.target.value)}
              className={cn(INPUT, "w-[145px]")}
              aria-label="Data di fine"
            />
          </label>
        )}

        {tipo.startsWith("barra") && (
          <label className="space-y-1">
            <span className="text-[11px] text-muted-foreground block">Modulo</span>
            <select
              value={moduloId}
              onChange={(e) => setModuloId(e.target.value)}
              className={cn(INPUT, "w-[190px]")}
              aria-label="Modulo del nuovo elemento"
            >
              <option value="">Fase libera</option>
              {moduli.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.codice} · {m.nome}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="space-y-1">
          <span className="text-[11px] text-muted-foreground block">Ordine</span>
          <input
            type="number"
            min={0}
            max={999}
            value={ordine}
            onChange={(e) => setOrdine(Number(e.target.value))}
            className={cn(INPUT, "w-[80px] tabular-nums")}
            aria-label="Ordine"
          />
        </label>

        <Button onClick={salva} disabled={!valido || crea.isPending}>
          {crea.isPending && <Loader2 className="size-4 animate-spin" />}
          Salva
        </Button>
        <Button variant="ghost" onClick={onFatto}>
          Annulla
        </Button>
      </div>

      {errore && (
        <p className="text-xs text-destructive inline-flex items-center gap-1.5">
          <AlertTriangle className="size-3.5" />
          {errore}
        </p>
      )}
    </div>
  );
}
